<template>
  <div class="container">
    <h1>Configure "{{object['name']}}"</h1>
    <b-tabs position="is-centered" class="block" v-model="activeTab">
      <b-tab-item label="Info">
        <div class="container margin_top_1rem has-text-left">
          <b-field :label="'ID: ' + object.id"></b-field>
          <b-field :label="'Object Type: ' + object.object_type_name"></b-field>
          <b-field label="Name">
            <b-input v-model="objectName" required></b-input>
          </b-field>
          <b-field label="Value Suffix">
            <b-input v-model="objectSuffix"></b-input>
          </b-field>
          <b-field label="Graph Template">
            <b-select v-model="graphTemplateId">
              <option value="0">Select one...</option>
              <option
                  v-for="graphTemplate in graphTemplates"
                  :key="graphTemplate.id"
                  :value="graphTemplate.id"
              >{{graphTemplate.name}}
              </option>
            </b-select>
          </b-field>
          <div class="container has-text-left margin_top_1rem margin_bottom_2rem" v-if="endpointAllowed">
            <b-field label="Endpoint:">
              <b-select v-model="endpointId">
                <option :value="0">choose one...</option>
                <option
                    v-for="endpoint in endpoints"
                    :key="endpoint.id"
                    :value="endpoint.id"
                >{{endpoint.deviceName}} - {{endpoint.name}}
                </option>
              </b-select>
            </b-field>
            <b-field v-if="urlForEndpoint.length > 0" label="URL:">{{urlForEndpoint}}</b-field>
            <b-field v-if="endpointId > 0" label="Endpoint ID:">{{endpointId}}</b-field>
          </div>
          <div class="block" v-if="endpointAllowed">
            <b-field label="Topic for disabled state:">
              <b-input v-model="disabledStateTopic" placeholder="Enter topic (optional)..."/>
            </b-field>
          </div>
          <div v-if="hasExtraField">
            <b-field label="Extra">
              <b-input type="text" v-model="extra" placeholder="For slider, define presets..."/>
            </b-field>
            <p>Presets should have a format of: LABEL1=VALUE1;LABEL2=VALUE2;...</p>
            <p>For dropdowns enter name of the topic from which to read the mappings...</p>
          </div>
          <div class="container margin_top_1rem margin_bottom_2rem" v-if="isCamera">
            <b-field label="Associated camera:">
              <b-select v-model="cameraId">
                <option v-for="camera in cameraList"
                        :key="camera.id"
                        :value="camera.id"
                >{{camera.name}}
                </option>
              </b-select>
            </b-field>
          </div>
          <div class="buttons" style="margin-top: 1.5rem;">
            <b-button type="is-danger-outlined" @click="handleBack">Back</b-button>
            <b-button type="is-success" @click="handleModifyObject" :disabled="!validObject()">Modify
            </b-button>
          </div>
        </div>
      </b-tab-item>
      <b-tab-item label="State construction" v-if="object.object_type_id !== 31">
        <ul v-if="object.propRules && object.propRules.length > 0">
          <li v-for="(propRule, index) in object.propRules"
              v-bind:key="index"
          >
            <app-prop-rule :objectId="object.id" :data="propRule"
                           :actionTypes="object.actionTypes"></app-prop-rule>
          </li>
        </ul>
        <div class="container margin_top_1rem" v-else>
          <app-add-prop-rule :object="object" :handleBack="() => {activeTab = 0;}"></app-add-prop-rule>
        </div>
      </b-tab-item>
      <b-tab-item label="Delete">
        <div class="container margin_top_1rem">
          <div class="margin_bottom_1rem" v-if="!validDelete()">Object cannot be deleted because it has state
            rules.
          </div>
          <b-button type="is-danger" @click="handleDelete" :disabled="!validDelete()">Delete Object</b-button>
        </div>
      </b-tab-item>
    </b-tabs>
    <hr />
    <div class="block">
      <b-field label="Set object state:">
        <b-input v-model="objectState" placeholder="Enter value..." />
      </b-field>
    </div>
    <div class="buttons">
      <b-button type="is-success" class="is-small" @click="handleClickSetObjectState">Set</b-button>
    </div>
  </div>
</template>

<script>
  import {mapGetters, mapActions} from 'vuex';
  import BField from "buefy/src/components/field/Field";
  import BInput from "buefy/src/components/input/Input";
  import BSelect from "buefy/src/components/select/Select";
  import BTabItem from "buefy/src/components/tabs/TabItem";
  import BButton from "buefy/src/components/button/Button";
  import AddPropRule from '../components/configure/AddPropRule';
  import PropRule from '../components/configure/PropRule';

  export default {
    name: "screenconfigureobject",
    components: {
      BButton,
      BTabItem,
      BSelect,
      BInput,
      BField,
      appPropRule: PropRule,
      appAddPropRule: AddPropRule
    },
    data() {
      return {
        activeTab: 0,
        objectName: '',
        objectSuffix: '',
        roomId: 0,
        endpointId: 0,
        endpointAllowed: false,
        graphTemplateId: 0,
        cameraId: 0,
        disabledStateTopic: '',
        extra: '',
        objectState: '',
      };
    },
    computed: {
      ...mapGetters('ui', ['loadingOverlay']),
      ...mapGetters('objects', ['objectById']),
      ...mapGetters('cameras', ['cameras']),
      ...mapGetters('rooms', ['rooms']),
      ...mapGetters('graphs', ['graphTemplates']),
      ...mapGetters('devices', ['devices', 'deviceEndpoints']),
      ...mapGetters('integrations', ['integrations']),
      object() {
        return this.objectById(this.$route.params.id);
      },
      endpoints() {
        const ret = [];

        this.devices.forEach(item => {
          item.endpoints.forEach(endpoint => {
            ret.push({
              deviceId: item.id,
              deviceName: item.name,
              ...endpoint
            });
          });
        });

        return ret;
      },
      urlForEndpoint() {
        let url = '';

        if (this.endpointId > 0) {
          this.endpoints.forEach(endpoint => {
            if (parseInt(endpoint.id) === parseInt(this.endpointId)) {
              url = endpoint.url;
            }
          });
        }

        return url;
      },
      isCamera() {
        return parseInt(this.object.object_type_id) === 31;
      },
      cameraList() {
        const ret = [
          {
            id: 0,
            name: 'Choose...'
          }
        ];
        this.cameras.forEach(item => {
          ret.push({
            ...item
          });
        });

        return ret;
      },
      hasExtraField() {
        return this.object.object_type_id === 37 || this.object.object_type_id === 38;
      }
    },
    methods: {
      ...mapActions('objects', ['deleteObject', 'modifyObject', 'addEndpointToObject', 'setObjectState']),
      validDelete() {
        if (this.object.propRules && this.object.propRules.length > 0) {
          return false;
        }

        return true;
      },
      validObject() {
        return true;
      },
      handleModifyObject() {
        this.modifyObject({
          id: this.object.id,
          name: this.objectName,
          suffix: this.objectSuffix.trim(),
          roomId: this.roomId,
          cameraId: this.cameraId,
          graphTemplateId: this.graphTemplateId,
          commandCallEndpointId: this.endpointId > 0 ? this.endpointId : 0,
          disabledStateTopic: this.disabledStateTopic,
          extra: this.extra.trim()
        });
      },
      handleClickSetObjectState() {
        if (confirm('Are you sure ?')) {
          this.setObjectState({
            id: this.object.id,
            value: this.objectState
          });
        }
      },
      handleBack() {
        this.$router.push('/configure/objects');
      },
      handleDelete() {
        if (confirm('Are you sure ?')) {
          this.deleteObject(this.object.id);
          this.$router.push('/configure/objects');
        }
      }
    },
    mounted() {
      this.objectName = this.object.name;
      this.objectSuffix = this.object.suffix;
      this.cameraId = this.object.camera_id;
      this.graphTemplateId = this.object.graph_template_id;
      this.disabledStateTopic = this.object.disabled_state_topic;
      this.extra = this.object.extra;

      if (this.object['command_call_endpoint'] > 0) {
        this.endpointId = parseInt(this.object['command_call_endpoint']);
      }

      this.objectState = this.object['state'];

      if (this.object.object_type_id === 7 ||
        this.object.object_type_id === 16 ||
        this.object.object_type_id === 19 ||
        this.object.object_type_id === 30 ||
        this.object.object_type_id === 36 ||
        this.object.object_type_id === 37 ||
        this.object.object_type_id === 38
      ) {
        this.endpointAllowed = true;
      }
    }
  }
</script>

<style scoped>
  h1 {
    font-size: 1.5rem;
    font-weight: bold;
    margin-top: 0.75rem;
    margin-bottom: 0.75rem;
  }

  .margin_top_1rem {
    margin-top: 1rem;
  }

  .margin_bottom_1rem {
    margin-bottom: 1rem;
  }

  .margin_bottom_2rem {
    margin-bottom: 2rem;
  }
</style>
